// src/pages/LikesPage.js

import React, { useContext, useMemo } from 'react';
import { AuthContext }     from '../context/AuthContext';
import { FilterContext }   from '../context/FilterContext';
import { LocationContext } from '../context/LocationContext';
import { ModalContext }    from '../context/ModalContext';
import { TabContext }      from '../context/TabContext';

import FavoritePetCard from '../components/FavoritePetCard';
import ModalSignIn     from '../components/ModalSignIn';
import Spinner         from '../components/Spinner';
import './LikesPage.css';

// ✨ Hook basado en React Query con `enabled` y `retry`
import { useFavorites } from '../hooks/rq/useFavorites';

const LikesPage = () => {
  const { isAuthenticated, user }  = useContext(AuthContext);
  const { filters }                = useContext(FilterContext);
  const { coords }                 = useContext(LocationContext);
  const { openModal, modalStacks } = useContext(ModalContext);
  const { activeTab }              = useContext(TabContext);

  // 1) Ubicación efectiva: perfil → filtro → geolocalización
  const effectiveLocation = useMemo(() => {
    if (user?.latitude != null && user?.longitude != null) {
      return { lat: user.latitude, lng: user.longitude };
    }
    if (filters.location?.lat != null && filters.location?.lng != null) {
      return { lat: filters.location.lat, lng: filters.location.lng };
    }
    return { lat: coords.lat, lng: coords.lng };
  }, [
    user?.latitude, user?.longitude,
    filters.location?.lat, filters.location?.lng,
    coords.lat, coords.lng
  ]);

  // 2) React Query: cargar favoritos según la ubicación
  //    * enabled: sólo si isAuthenticated
  //    * retry: evita bucles en 401
  const {
    data: favorites = [],
    isLoading,
    isError,
    error,
    refetch
  } = useFavorites(effectiveLocation);

  // ── Helpers para abrir ModalSignIn (login/register) ───────────
  const isSignInOpenAnywhere = () =>
    Object.values(modalStacks).some(stack =>
      stack.some(m => m.metadata?.modalType === 'signIn')
    );

  const handleOpenLogin = () => {
    if (isSignInOpenAnywhere()) return;
    openModal(
      <ModalSignIn initialTab="login" />,
      { tab: activeTab, metadata: { modalType: 'signIn' }, overlayClassName: 'pet-detail-overlay' }
    );
  };

  const handleOpenRegister = () => {
    if (isSignInOpenAnywhere()) return;
    openModal(
      <ModalSignIn initialTab="register" />,
      { tab: activeTab, metadata: { modalType: 'signIn' }, overlayClassName: 'pet-detail-overlay' }
    );
  };

  // ── 3) Renderizado según estado ────────────────────────────────

  // 🚧 Si no está autenticado, pedimos login/register
  if (!isAuthenticated) {
    return (
      <div className="likes-page-outer">
        <div className="likes-page-inner not-authenticated">
          <p>Para ver tus favoritos debes iniciar sesión.</p>
          <div className="auth-options-column">
            <button className="btn" onClick={handleOpenLogin}>Iniciar sesión</button>
            <div className="separator-container"><span className="separator">o</span></div>
            <button className="btn" onClick={handleOpenRegister}>Crear cuenta</button>
          </div>
        </div>
      </div>
    );
  }

  // ⚙️ Mientras se cargan (primer fetch) mostramos spinner
  if (isLoading) {
    return (
      <div className="likes-page-outer">
        <div
          className="likes-page-inner"
          style={{ display: 'flex', justifyContent: 'center', paddingTop: '50px' }}
        >
          <Spinner
            size={222}
            spinnerText="Cargando"
            showEllipses
            pulseScale={1.008}
            pulseDuration={1}
            textCycleDuration={1.6}
            textColor="black"
            textSize="0.7rem"
            textOffsetX="-12px"
            textOffsetY="20px"
            containerOffsetY="80%"
          />
        </div>
      </div>
    );
  }

  // ❗ Error al cargar favoritos
  if (isError) {
    return (
      <div className="likes-page-outer">
        <div className="likes-page-inner" style={{ textAlign: 'center', padding: '20px' }}>
          <p>Error cargando favoritos.</p>
          <pre style={{ color: 'red' }}>{error.message}</pre>
          <button className="btn" onClick={refetch}>Reintentar</button>
        </div>
      </div>
    );
  }

  // 🚫 No hay favoritos tras cargar
  if (favorites.length === 0) {
    return (
      <div className="likes-page-outer">
        <div className="likes-page-inner">
          <p>No tienes mascotas favoritas. Dale like a alguna para que aparezca aquí.</p>
        </div>
      </div>
    );
  }

  // ✅ Renderizamos la lista de favoritos
  return (
    <div className="likes-page-outer">
      <div className="likes-page-inner">
        {favorites.map(pet => (
          <FavoritePetCard key={pet.id} pet={pet} />
        ))}
      </div>
    </div>
  );
};

export default LikesPage;
